import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import styled from 'styled-components';
import theme from '@styles/theme';
import { useRouteHandler } from '@/shared/hooks/useRouteHandler';
import Loader from '@/shared/components/molecules/Loader';
import ConfirmModal from '@/shared/components/modal/ConfirmModal';
import StyledButton from '@/shared/components/atoms/StyledButton';

type FullScreenSectionProps = {
	isEdit?: boolean;
	loading?: boolean;
	showLeaveConfirm?: boolean;
	onClickButton?: () => void;
	children: ReactNode;
}

const FullScreenLayout = ({
	isEdit,
	loading = false,
	showLeaveConfirm = false,
	onClickButton,
	children
}: FullScreenSectionProps) => {
	const navigate = useNavigate();
	const [showModal, setShowModal] = useState(false);
	const { title, subTitle } = useRouteHandler();
	
	// 브라우저 닫기/새로고침 경고
	const handleBeforeunload = useCallback((e: BeforeUnloadEvent) => {
		if (!showLeaveConfirm) return;
		e.preventDefault();
		e.returnValue = ''; // 레거시용
	}, [showLeaveConfirm]);
	
	// 라우터 네비게이션 블로킹
	const blocker = useBlocker(({ currentLocation, nextLocation }) => (
		showLeaveConfirm && currentLocation.pathname !== nextLocation.pathname
	));
	
	const handleConfirmLeave = useCallback(() => {
		setShowModal(false);
		if (blocker.state === 'blocked') {
			blocker.proceed(); // 네비게이션 허용
		}
	}, [blocker]);
	
	const handleCancelLeave = useCallback(() => {
		setShowModal(false);
		if (blocker.state === 'blocked') {
			blocker.reset(); // 블로킹 유지
		}
	}, [blocker]);
	
	const handleGoBack = () => {
		navigate(-1);
	};
	
	useEffect(() => {
		if (blocker.state === 'blocked') {
			setShowModal(true);
		}
	}, [blocker.state]);
	
	useEffect(() => {
		if (showLeaveConfirm) {
			window.addEventListener('beforeunload', handleBeforeunload);
			return () => {
				window.removeEventListener('beforeunload', handleBeforeunload);
			};
		}
	}, [showLeaveConfirm, handleBeforeunload]);
	
	return (
		<>
			{loading && <Loader disabled={true} />}
			<Header>
				<HeaderContainer>
					 <BackButton onClick={handleGoBack}>
	                    <IoArrowBack fontSize={22} />
	                    <ButtonText>뒤로가기</ButtonText>
	                </BackButton>
					<ButtonGroup>
						<StyledButton
							width={82}
							height={35}
							fontSize={15}
							fontColor={theme.colors.whiteColor}
							fontFamily="PretendardRegular"
							bgColor={isEdit ? theme.colors.activeOrange :theme.colors.activeBlue}
							title={isEdit ? '수정하기' : '등록하기'}
							onClick={onClickButton}
						/>
					</ButtonGroup>
				</HeaderContainer>
			</Header>
			<Section>
				<PageInfo>
					<Title>{title}</Title>
					{subTitle && <SubTitle>{subTitle}</SubTitle>}
				</PageInfo>
				{children}
			</Section>
			{showModal && (
				<ConfirmModal
					onConfirm={handleConfirmLeave}
					onCancel={handleCancelLeave}
				/>
			)}
		</>
	);
};

export default FullScreenLayout;

const Header = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 5;
  border-bottom: 1px solid ${theme.colors.ultraLightGrayBorder};
  background-color: ${theme.colors.whiteColor};
`;
const HeaderContainer = styled.div`
  max-width: 1280px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
`;
const Section = styled.article`
  width: 1280px;
  padding: 32px 0 64px;
  margin: 0 auto;
`;
const BackButton = styled.div`
  height: 34px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const ButtonText = styled.span`
  width: 74px;
  text-align: center;
  font-family: PretendardRegular, sans-serif;
  padding-top: 2px;

  ${BackButton}:hover & {
    font-family: PretendardMedium, sans-serif;
  }
`;
const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
`;
const PageInfo = styled.div`
  padding-bottom: 28px;
  border-bottom: 1px solid ${theme.colors.ultraLightGrayBorder};
`;
const Title = styled.h3`
  ${theme.typography.h2};
  font-family: PretendardSemiBold, sans-serif;
  margin-bottom: 10px;
`;
const SubTitle = styled.h4`
  ${theme.typography.body2};
  color: ${theme.colors.deepGrayFontColor};
`;
